/**
 * What happens when the PPSR check comes back with an existing interest.
 *
 * Winnie, 18 September. Two paths, chosen by seller type, "because a private seller and a
 * licensed dealer are in genuinely different positions to actually clear their own
 * encumbrance":
 *
 *   Type 1 / 2 (private individual, private company) → PayoutLetterSplit
 *   Type 3 / 4 / 5 (LMCT, auction, tender)           → SellerSelfClears
 *
 * Both start the same way — Sales and Finance are told the moment the check comes back —
 * and what differs is what the deal then requires.
 *
 * ---------------------------------------------------------------------------------------
 * THE PART WINNIE ASKED TO BE FLAGGED BEFORE BUILDING, AND THE ANSWER
 *
 * "This is a real extension to Payment Run (AP)'s data model — it needs to support two
 * payment lines against one deal, not just one. Flag this against whatever the current
 * Payment Run entity looks like before building; if it's hard-coded to a single payee per
 * deal, that's the actual piece of work here, not the payout letter itself."
 *
 * Checked, and it is hard-coded to a single payee. `payment_runs` holds one row per deal with
 * one `total_amount`; `payablesService` reads the payee from `deals.seller_name` and the bank
 * details from `deals.seller_bsb` / `seller_account_number`; the ABA file is built from those
 * same three fields. There was nowhere for a second payee to exist.
 *
 * `payment_run_lines` is that piece of work, and it is deliberately additive: a run with no
 * lines behaves exactly as it always has, and a run with lines pays those payees instead. The
 * two-approver gate is untouched — it guards the run, and it still does.
 * ---------------------------------------------------------------------------------------
 */
import { supabase } from '../lib/supabase'
import { sellerIssuesOwnInvoice } from '../lib/sellerType'

export type ResolutionPath = 'PayoutLetterSplit' | 'SellerSelfClears'

export interface EncumbranceResolution {
  id: string
  dealId: string
  resolutionPath: ResolutionPath
  detectedAt: string
  notifiedAt: string | null
  resolvedAt: string | null
}

export interface FinalPayoutLetter {
  id: string
  dealId: string
  documentPath: string | null
  documentName: string | null
  payoutAmount: number | null
  payoutRecipient: string | null
  bankBsb: string | null
  bankAccount: string | null
  bankName: string | null
  uploadedAt: string
}

/**
 * Which path a seller type takes.
 *
 * A licensed seller clears its own encumbrance as a matter of course — it is in the business
 * of doing so and the money is its own. A private seller usually cannot: the financier holds
 * the interest and has to be paid directly, which is what the split exists for.
 */
export function resolutionPathFor(sellerType?: string | null): ResolutionPath {
  /* The same three seller types that issue their own invoice are the licensed ones - dealer,
     auction, tender - so the platform's existing definition decides this rather than a second
     list that could drift from it. */
  return sellerIssuesOwnInvoice(sellerType) ? 'SellerSelfClears' : 'PayoutLetterSplit'
}

export async function loadResolution(dealId: string): Promise<EncumbranceResolution | null> {
  const { data, error } = await supabase
    .from('encumbrance_resolutions')
    .select('*')
    .eq('deal_id', dealId)
    .maybeSingle()
  if (error) throw error
  const r = data as Record<string, unknown> | null
  return r
    ? {
        id: String(r.id),
        dealId: String(r.deal_id),
        resolutionPath: r.resolution_path as ResolutionPath,
        detectedAt: String(r.detected_at || ''),
        notifiedAt: (r.notified_at as string) || null,
        resolvedAt: (r.resolved_at as string) || null,
      }
    : null
}

export async function loadPayoutLetter(dealId: string): Promise<FinalPayoutLetter | null> {
  const { data, error } = await supabase
    .from('final_payout_letters')
    .select('*')
    .eq('deal_id', dealId)
    .maybeSingle()
  if (error) throw error
  const r = data as Record<string, unknown> | null
  return r
    ? {
        id: String(r.id),
        dealId: String(r.deal_id),
        documentPath: (r.document_path as string) || null,
        documentName: (r.document_name as string) || null,
        payoutAmount: r.payout_amount == null ? null : Number(r.payout_amount),
        payoutRecipient: (r.payout_recipient as string) || null,
        bankBsb: (r.payout_recipient_bank_bsb as string) || null,
        bankAccount: (r.payout_recipient_bank_acct as string) || null,
        bankName: (r.payout_recipient_bank_name as string) || null,
        uploadedAt: String(r.uploaded_at || ''),
      }
    : null
}

/**
 * Open a resolution when a PPSR check finds an existing interest.
 *
 * Idempotent on the deal: a re-run of the check against a vehicle that is already known to be
 * encumbered must not create a second resolution or re-notify anybody.
 */
export async function openResolution(
  dealId: string,
  sellerType?: string | null,
  vin?: string | null,
): Promise<EncumbranceResolution | null> {
  try {
    const existing = await loadResolution(dealId)
    if (existing) return existing

    const path = resolutionPathFor(sellerType)
    const { data, error } = await supabase
      .from('encumbrance_resolutions')
      .insert([
        {
          deal_id: dealId,
          vin: vin || null,
          seller_type: sellerType || null,
          resolution_path: path,
          notified_at: new Date().toISOString(),
        },
      ])
      .select('*')
      .single()
    if (error) throw error
    const r = data as Record<string, unknown>
    return {
      id: String(r.id),
      dealId,
      resolutionPath: path,
      detectedAt: String(r.detected_at || ''),
      notifiedAt: (r.notified_at as string) || null,
      resolvedAt: null,
    }
  } catch (e) {
    /* An encumbrance we could not record is not a reason to fail the check that found it —
       the check's own result is still written, and the DD screen still shows the interest. */
    console.error('Encumbrance resolution could not be opened', e)
    return null
  }
}

export interface PayoutGate {
  ok: boolean
  reason: string
}

/**
 * May this deal's payment be released?
 *
 * Winnie: "require: FinalPayoutLetter is uploaded with payout_amount and payout_recipient
 * set." Only on the split path — every other deal pays the way it always has.
 */
export function payoutGate(
  path: ResolutionPath | null,
  letter: FinalPayoutLetter | null,
): PayoutGate {
  if (path !== 'PayoutLetterSplit') return { ok: true, reason: '' }
  if (!letter || !letter.documentPath) {
    return {
      ok: false,
      reason:
        'The PPSR check found an existing interest on this vehicle and the seller is a private seller, so the Final ' +
        'Payout Letter is compulsory before payment is released. Upload it on Purchase Details.',
    }
  }
  if (!letter.payoutAmount || letter.payoutAmount <= 0) {
    return { ok: false, reason: 'The payout letter has no payout amount on it. Record what the financier is owed.' }
  }
  if (!String(letter.payoutRecipient || '').trim()) {
    return { ok: false, reason: 'The payout letter has no recipient on it. Record who holds the existing interest.' }
  }
  return { ok: true, reason: '' }
}

export interface PaymentLine {
  lineType: 'payout' | 'seller'
  payee: string
  bsb: string | null
  accountNumber: string | null
  accountName: string | null
  amount: number
  sequence: number
}

/**
 * The lines a payment run should pay.
 *
 * The financier first, the seller the balance. A balance of zero or less produces no seller
 * line at all — where the payout swallows the whole purchase price there is nothing to send
 * the seller, and a zero-dollar line in a bank file is a line that gets rejected.
 */
export function splitLines(args: {
  purchasePrice: number
  payoutAmount: number
  payoutRecipient: string
  payoutBsb?: string | null
  payoutAccount?: string | null
  payoutAccountName?: string | null
  sellerName: string
  sellerBsb?: string | null
  sellerAccount?: string | null
  sellerAccountName?: string | null
}): PaymentLine[] {
  const price = Number(args.purchasePrice) || 0
  const payout = Math.min(Number(args.payoutAmount) || 0, price)
  const balance = Math.round((price - payout) * 100) / 100

  const lines: PaymentLine[] = [
    {
      lineType: 'payout',
      payee: args.payoutRecipient,
      bsb: args.payoutBsb || null,
      accountNumber: args.payoutAccount || null,
      accountName: args.payoutAccountName || args.payoutRecipient,
      amount: payout,
      sequence: 1,
    },
  ]

  if (balance > 0) {
    lines.push({
      lineType: 'seller',
      payee: args.sellerName,
      bsb: args.sellerBsb || null,
      accountNumber: args.sellerAccount || null,
      accountName: args.sellerAccountName || args.sellerName,
      amount: balance,
      sequence: 2,
    })
  }

  return lines
}

/** Write the split onto a payment run, replacing whatever was there. */
export async function writePaymentLines(
  paymentRunId: string,
  dealId: string,
  lines: PaymentLine[],
): Promise<void> {
  await supabase.from('payment_run_lines').delete().eq('payment_run_id', paymentRunId)
  if (!lines.length) return
  const { error } = await supabase.from('payment_run_lines').insert(
    lines.map((l) => ({
      payment_run_id: paymentRunId,
      deal_id: dealId,
      line_type: l.lineType,
      payee: l.payee,
      bsb: l.bsb,
      account_number: l.accountNumber,
      account_name: l.accountName,
      amount: l.amount,
      sequence: l.sequence,
    })),
  )
  if (error) throw error
}

export async function loadPaymentLines(paymentRunId: string): Promise<PaymentLine[]> {
  const { data, error } = await supabase
    .from('payment_run_lines')
    .select('*')
    .eq('payment_run_id', paymentRunId)
    .order('sequence')
  if (error) throw error
  return ((data || []) as unknown as Record<string, unknown>[]).map((r) => ({
    lineType: r.line_type as 'payout' | 'seller',
    payee: String(r.payee || ''),
    bsb: (r.bsb as string) || null,
    accountNumber: (r.account_number as string) || null,
    accountName: (r.account_name as string) || null,
    amount: Number(r.amount) || 0,
    sequence: Number(r.sequence) || 1,
  }))
}
